import { useState, useEffect, useLayoutEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import AddUser from '../Users/AddUser'
import PageModal from '../common/PageModal'
import Dropdown from '../common/Dropdown'
import SimpleButton from '../common/SimpleButton'
import GoogleAddressInput from '../common/GoogleAddressInput'
import UserSearchInput from '../common/UserSearchInput'
import { toast } from '../common/Toast'
import SimpleTextInput from '../common/SimpleTextInput'
import { loadCompanyRoles, clearCompanyRoles } from '../../actions/companies'
import { createBranch, putBranch } from '../../actions/branches'
import { formatPhone } from '../../utils/functions'
import countries from '../../utils/countries.json'
import { branchesWritePermission } from '../../utils/permissionValidation'
import './companies-management-modal.scss'

const AddEditBranch = (props) => {
  const { t } = useTranslation()
  const { selectedCompany, selectedBranch, companyRoles, closeModal, showSpinner, setShowSpinner, onSuccessCreateBranch } = props
  const [branchName, setBranchName] = useState('')
  const [country, setCountry] = useState('')
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [stateProvince, setStateProvince] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [phone, setPhone] = useState('')
  const [adminEmail, setAdminEmail] = useState(null)
  const [roleIds, setRoleIds] = useState([])
  const [haveError, setHaveError] = useState({})
  const [showAddUserModal, setShowAddUserModal] = useState(false)
  const phonePattern = new RegExp(/^[+]?[\s./0-9]*[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/g)
  const emailPattern = new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)

  useLayoutEffect(() => {
    if (!selectedCompany?.id) {
      return
    }

    props.actions.loadCompanyRoles(selectedCompany.id)
  }, [])

  useEffect(() => {
    if (companyRoles.length > 0) {
      setRoleIds(companyRoles.map((role) => role.id))
    }
  }, [companyRoles])

  useEffect(() => {
    if (selectedBranch) {
      setBranchName(selectedBranch.branchName)
      setCountry(selectedBranch.country || '')
      setAddress(selectedBranch.officeAddressOne || '')
      setCity(selectedBranch.city || '')
      setStateProvince(selectedBranch.stateProvinceRegion || '')
      setZipCode(selectedBranch.zipPostalCode || '')
      setAdminEmail(selectedBranch.email || '')
      setPhone(formatPhone(selectedBranch.telephone || ''))
    }
  }, [selectedBranch])

  useEffect(() => {
    if (selectedCompany) {
      setCountry(selectedCompany?.country || '')
      setAddress(selectedCompany?.officeAddressOne || '')
      setCity(selectedCompany?.city || '')
      setStateProvince(selectedCompany?.stateProvinceRegion || '')
      setZipCode(selectedCompany?.zipPostalCode || '')
      setPhone(formatPhone(selectedCompany?.telephone || ''))
    }
  }, [selectedCompany])

  const branchFooterContent = () => {
    return (
      <>
        <SimpleButton className="cancel-button" onClick={() => handleCloseModal()}>
          {t('cancel')}
        </SimpleButton>
        <SimpleButton
          className="submit-button"
          onClick={() => handleAddEditBranch()}
          disabled={showSpinner || hasSameInput()}
          requiredPermission={branchesWritePermission}
        >
          {t('save')}
        </SimpleButton>
      </>
    )
  }

  const hasSameInput = () => {
    return (
      selectedBranch &&
      branchName === selectedBranch.branchName &&
      country === selectedBranch.country &&
      address === selectedBranch.officeAddressOne &&
      city === selectedBranch.city &&
      stateProvince === selectedBranch.stateProvinceRegion &&
      zipCode === selectedBranch.zipPostalCode &&
      phone === formatPhone(selectedBranch.telephone || '')
    )
  }

  const isValidInput = () => {
    let errors = {}
    let isValid = true

    if (!branchName.trim()) {
      errors = { ...errors, branchName: true }
      isValid = false
    }

    if (!country.trim()) {
      errors = { ...errors, country: true }
      isValid = false
    }

    if (!address.trim()) {
      errors = { ...errors, address: true }
      isValid = false
    }

    if (!city.trim()) {
      errors = { ...errors, city: true }
      isValid = false
    }

    if (!stateProvince.trim()) {
      errors = { ...errors, stateProvince: true }
      isValid = false
    }

    if (!zipCode.trim()) {
      errors = { ...errors, zipCode: true }
      isValid = false
    }

    if (!phone.trim() || !phonePattern.test(phone)) {
      errors = { ...errors, phone: true }
      isValid = false
    }

    if (!selectedBranch && !adminEmail && !emailPattern.test(adminEmail)) {
      errors = { ...errors, adminEmail: true }
      isValid = false
    }

    setHaveError(errors)
    return isValid
  }

  const handleAddEditBranch = () => {
    if (!isValidInput()) {
      toast.error(t('error.fill_all_fields'))
      return
    }

    let bodyParams = {
      branchName: branchName.trim(),
      email: adminEmail,
      country: country.trim(),
      officeAddressOne: address.trim(),
      city: city.trim(),
      stateProvinceRegion: stateProvince.trim(),
      zipPostalCode: zipCode.trim(),
      telephone: phone.trim(),
    }

    if (selectedBranch) {
      props.actions.putBranch(selectedBranch.company?.id, selectedBranch.id, bodyParams).then(() => {
        handleCloseModal()
      })

      return
    }

    bodyParams = { ...bodyParams, companyId: selectedCompany.id }

    props.actions.createBranch(selectedCompany.id, bodyParams).then(() => {
      if (onSuccessCreateBranch) {
        onSuccessCreateBranch(selectedCompany.id)
      }
      handleCloseModal()
    })
  }

  const handleCloseModal = () => {
    props.actions.clearCompanyRoles()
    closeModal()
  }

  const handleAddUser = (newUserCreated) => {
    setAdminEmail(newUserCreated.email)
    setPhone(formatPhone(newUserCreated.phone || ''))
    setCountry(newUserCreated.country || '')
    setAddress(newUserCreated.addressLine1 || '')
    setCity(newUserCreated.city || '')
    setStateProvince(newUserCreated.state || '')
    setZipCode(newUserCreated.zip || '')
  }

  const normalizeAccountType = (rawType) => {
    if (!rawType) return ''

    return rawType
      .replace(/_/g, ' ')
      .split(' ') 
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  return (
    <>
      <PageModal
        toggle
        onToggle={() => handleCloseModal()}
        className="companies-management-modal"
        title={selectedBranch ? t('organization_context.edit_branch') : t('organization_context.add_branch')}
        footerContent={branchFooterContent()}
      >
        <div className="companies-management-modal-content">
          <div className="section-title">{t('general_information').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('organization')}</p>
              <div className="value">{selectedBranch?.company?.name || selectedCompany?.companyName}</div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('name')}*</p>
              <SimpleTextInput
                value={branchName}
                onChange={(value) => setBranchName(value)}
                hasError={haveError['branchName']}
                onClear={() => setBranchName('')}
              />
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">
                {t('admin')}*
                {!selectedBranch && (
                  <span className="action-label" onClick={() => setShowAddUserModal(true)}>
                    {' '}
                    {t('create_user')}
                  </span>
                )}
              </p>
              {!selectedBranch && (
                <UserSearchInput
                  initialSelectedKey={adminEmail}
                  onChange={(option) => setAdminEmail(option.key)}
                  hasError={haveError['adminEmail']}
                  defaultQueryParams={{ roleId: roleIds, status: 'activated' }}
                  localFilters={{ branchName: null }}
                  setShowSpinner={setShowSpinner}
                />
              )}
              {selectedBranch && <div className="value">{selectedBranch.email}</div>}
            </div>
          </div>
          <div className="separator" />
          <div className="section-title">{t('location').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('phone')}*</p>
              <SimpleTextInput value={formatPhone(phone)} onChange={(value) => setPhone(value)} hasError={haveError['phone']} onClear={() => setPhone('')} />
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('country')}*</p>
              <Dropdown
                className="dropdown-input"
                placeholder={t('users-management.select-country')}
                options={countries?.map((country) => ({ key: country['code'], value: country['name'] }))}
                initialSelectedKey={country}
                onChange={(country) => setCountry(country.key)}
                hasError={haveError['country']}
                withFiltering
              />
            </div>
          </div>
          <GoogleAddressInput
            country={country}
            address={address}
            handleSetAddress={(e) => setAddress(e)}
            city={city}
            handleSetCity={(e) => setCity(e)}
            stateProvince={stateProvince}
            handleSetStateProvince={(e) => setStateProvince(e)}
            zipCode={zipCode}
            handleSetZipCode={(e) => setZipCode(e)}
            errors={haveError}
            withoutPlaceholders
          />
        </div>
      </PageModal>
      <div className="users-management-wrapper">
        {showAddUserModal && (
          <AddUser
            accountType={normalizeAccountType(selectedCompany?.accountType)}
            onCreateUser={(newUserCreated) => handleAddUser(newUserCreated)}
            closeModal={() => setShowAddUserModal(false)}
            showSpinner={showSpinner}
            setShowSpinner={setShowSpinner}
          />
        )}
      </div>
    </>
  )
}

function stateToProps({ companies }) {
  return {
    companyRoles: companies?.roles || [],
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        createBranch,
        putBranch,
        loadCompanyRoles,
        clearCompanyRoles,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(AddEditBranch)

